import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Input,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Save as SaveIcon,
  Search,
  ShieldCheck,
  Trash2
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsRolesListQueryKey,
  organizationsRolesPartialUpdateMutation,
  permissionsListQueryKey,
  type OrganizationRoleFull,
  type PermissionsLocalized
} from 'src/api/main'
import { MonoValue, ProTableContainer } from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { useDebouncedValue, useInvalidatePredicateQueries } from 'src/hooks'
import { readLocalized, toBaseLang, useShowApiErrors } from 'src/utils'

import { permissionMatchesQuery, type LocalizedPermission } from './utils'

const { Text } = Typography

type Props = {
  permissions: PermissionsLocalized[]
  roles: OrganizationRoleFull[]
  onEditRole: (role: OrganizationRoleFull) => void
  onDeleteRole: (role: OrganizationRoleFull) => void
}

/** roleId → ids of the permissions that role grants. */
type GrantMap = Record<number, Set<number>>

const rolePermissionIds = (role: OrganizationRoleFull): number[] =>
  Array.isArray(role.permissions) ? role.permissions.map((p) => p.id) : []

const buildGrantMap = (roles: OrganizationRoleFull[]): GrantMap => {
  const map: GrantMap = {}
  for (const role of roles) {
    map[role.id] = new Set(rolePermissionIds(role))
  }

  return map
}

const sameGrants = (a: Set<number>, b: Set<number>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id))

type CompactRoleHeaderProps = {
  role: OrganizationRoleFull
  allChecked: boolean
  someChecked: boolean
  onToggleAll: (checked: boolean) => void
  onEdit: (role: OrganizationRoleFull) => void
  onDelete: (role: OrganizationRoleFull) => void
}

/**
 * Single-row column header sized for checkbox cells: tri-state select-all,
 * role name (description in the tooltip), admin shield, ⋯ menu for
 * edit/delete. The platform matrix keeps the roomier `RoleColumnHeader`.
 */
const CompactRoleHeader = ({
  role,
  allChecked,
  someChecked,
  onToggleAll,
  onEdit,
  onDelete
}: CompactRoleHeaderProps) => {
  const { t } = useTranslation()
  const isAdmin = role.is_admin

  return (
    <Flex align="center" justify="center" gap={4}>
      <Checkbox
        checked={isAdmin || allChecked}
        indeterminate={!isAdmin && someChecked && !allChecked}
        disabled={isAdmin}
        aria-label={`${t('workspace.apps.roles.selectAll')} · ${role.name}`}
        onChange={(e) => onToggleAll(e.target.checked)}
      />
      <Tooltip title={role.description || role.name}>
        <Text strong style={{ fontSize: 13, minWidth: 0 }} ellipsis>
          {role.name}
        </Text>
      </Tooltip>
      {isAdmin ? (
        <Tooltip title={t('workspace.apps.roles.adminHasFullAccess')}>
          <ShieldCheck
            size={13}
            color="var(--ant-color-primary)"
            style={{ flex: '0 0 auto' }}
          />
        </Tooltip>
      ) : null}
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'edit',
              label: t('workspace.apps.roles.edit'),
              icon: <Pencil size={14} />,
              onClick: () => onEdit(role)
            },
            { type: 'divider' },
            {
              key: 'delete',
              label: t('delete'),
              icon: <Trash2 size={14} />,
              danger: true,
              disabled: isAdmin,
              onClick: () => onDelete(role)
            }
          ]
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<MoreHorizontal size={14} />}
          aria-label={t('workspace.apps.roles.columns.actions')}
        />
      </Dropdown>
    </Flex>
  )
}

/**
 * Role × permission matrix for third-party (custom) apps. Their permissions
 * are flat single codes — each row is one permission and a cell is a binary
 * granted/not-granted checkbox, unlike the platform-app matrix where a cell
 * bundles several structured `action_resource:scope` permissions.
 */
export const ThirdPartyRolesMatrix = ({
  permissions,
  roles,
  onEditRole,
  onDeleteRole
}: Props) => {
  const { t, i18n } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const lang = toBaseLang(i18n.language)

  const { mutateAsync: updateRole, isPending: isUpdating } = useMutation({
    ...organizationsRolesPartialUpdateMutation()
  })

  const initial = useMemo(() => buildGrantMap(roles), [roles])
  // null = pristine; toggling clones the effective map so Reset is just
  // dropping the draft.
  const [draft, setDraft] = useState<GrantMap | null>(null)
  const effective = draft ?? initial

  // The draft is a snapshot built from `roles` at first toggle. If `roles`
  // changes underneath an active draft (a sibling edit/add refetches the list),
  // unchanged roles would be flagged dirty and saved with a stale/empty grant
  // set, clobbering server state. Discard the draft when the role+permission
  // signature actually changes (identity-only refetches keep edits intact).
  // Adjusting state during render is React's recommended alternative to a
  // setState-in-effect here.
  const rolesSignature = useMemo(
    () =>
      roles
        .map(
          (role) =>
            `${role.id}:${rolePermissionIds(role)
              .sort((a, b) => a - b)
              .join(',')}`
        )
        .join('|'),
    [roles]
  )
  const [seenSignature, setSeenSignature] = useState(rolesSignature)
  if (seenSignature !== rolesSignature) {
    setSeenSignature(rolesSignature)
    setDraft(null)
  }

  const dirtyRoleIds = useMemo(() => {
    if (!draft) return []

    return roles
      .filter((role) => !role.is_admin)
      .filter(
        (role) =>
          !sameGrants(
            draft[role.id] ?? new Set(),
            initial[role.id] ?? new Set()
          )
      )
      .map((role) => role.id)
  }, [draft, initial, roles])
  const dirty = dirtyRoleIds.length > 0

  // Toggled-cell count for the toolbar ("3 unsaved changes").
  const changedCellCount = useMemo(() => {
    if (!draft) return 0
    let count = 0
    for (const role of roles) {
      if (role.is_admin) continue
      const next = draft[role.id] ?? new Set<number>()
      const prev = initial[role.id] ?? new Set<number>()
      for (const id of next) if (!prev.has(id)) count += 1
      for (const id of prev) if (!next.has(id)) count += 1
    }

    return count
  }, [draft, initial, roles])

  const sortedPermissions = useMemo(
    () => [...permissions].sort((a, b) => a.code.localeCompare(b.code)),
    [permissions]
  )

  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search.trim().toLowerCase(), 200)

  const visiblePermissions = useMemo(
    () =>
      sortedPermissions.filter((p) =>
        permissionMatchesQuery(p, debounced, lang)
      ),
    [debounced, lang, sortedPermissions]
  )

  const setRoleGrants = (
    roleId: number,
    mutate: (grants: Set<number>) => void
  ) => {
    setDraft((prev) => {
      const base = prev ?? initial
      const next: GrantMap = {}
      for (const role of roles) {
        next[role.id] = new Set(base[role.id])
      }
      mutate(next[roleId])

      return next
    })
  }

  const toggle = (roleId: number, permissionId: number, checked: boolean) =>
    setRoleGrants(roleId, (grants) => {
      if (checked) grants.add(permissionId)
      else grants.delete(permissionId)
    })

  // Scoped to the rows currently on screen so "select all" during a search
  // never silently flips permissions the user can't see.
  const setAllForRole = (roleId: number, checked: boolean) =>
    setRoleGrants(roleId, (grants) => {
      visiblePermissions.forEach((p) => {
        if (checked) grants.add(p.id)
        else grants.delete(p.id)
      })
    })

  const onSave = async () => {
    if (!draft) return
    try {
      await Promise.all(
        roles
          .filter((role) => dirtyRoleIds.includes(role.id))
          .map((role) =>
            updateRole({
              path: { id: role.id },
              body: {
                name: role.name,
                description: role.description ?? '',
                permissions_ids: [...(draft[role.id] ?? [])]
              }
            })
          )
      )
      await invalidateQueries([
        organizationsRolesListQueryKey(),
        permissionsListQueryKey()
      ])
      setDraft(null)
      message.success(t('workspace.apps.roles.permissionsUpdated'))
    } catch (error) {
      showApiErrors(error)
    }
  }

  const columns: ColumnsType<PermissionsLocalized> = [
    {
      title: t('workspace.apps.roles.matrix.permission'),
      key: 'permission',
      width: 280,
      fixed: 'left' as const,
      render: (_, record) => {
        const perm = record as LocalizedPermission
        const labelText = readLocalized(perm.label, lang) || record.code
        const descriptionText = readLocalized(perm.description, lang)

        return (
          <Flex vertical style={{ lineHeight: 1.3 }}>
            <Tooltip title={descriptionText || undefined}>
              <Text strong style={{ fontSize: 13 }}>
                {labelText}
              </Text>
            </Tooltip>
            <MonoValue copyable={false} fontSize={11}>
              {record.code}
            </MonoValue>
          </Flex>
        )
      }
    },
    ...roles.map((role) => {
      const grants = effective[role.id] ?? new Set<number>()

      return {
        title: (
          <CompactRoleHeader
            role={role}
            allChecked={
              visiblePermissions.length > 0 &&
              visiblePermissions.every((p) => grants.has(p.id))
            }
            someChecked={visiblePermissions.some((p) => grants.has(p.id))}
            onToggleAll={(checked) => setAllForRole(role.id, checked)}
            onEdit={onEditRole}
            onDelete={onDeleteRole}
          />
        ),
        key: `role-${role.id}`,
        width: 140,
        align: 'center' as const,
        render: (_: unknown, record: PermissionsLocalized) => {
          const granted = role.is_admin || grants.has(record.id)
          const labelText =
            readLocalized((record as LocalizedPermission).label, lang) ||
            record.code

          return (
            <Checkbox
              checked={granted}
              disabled={role.is_admin}
              aria-label={`${role.name} · ${labelText}`}
              onChange={(e) => toggle(role.id, record.id, e.target.checked)}
            />
          )
        }
      }
    })
  ]

  return (
    <ProTableContainer<PermissionsLocalized>
      columnsReorderable={false}
      options={false}
      showSearch={false}
      rowKey="id"
      bordered
      sticky
      dataSource={visiblePermissions}
      columns={columns}
      pagination={false}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('workspace.apps.roles.emptyPermissionsCatalog')}
          />
        )
      }}
      leftChildren={
        <Input
          size="small"
          prefix={<Search size={14} />}
          placeholder={t('workspace.apps.roles.searchPermissions')}
          style={{ width: FILTER_CONTROL_WIDTH }}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      }
      rightChildren={
        <Flex gap={8} align="center">
          {changedCellCount > 0 ? (
            <Text
              type="secondary"
              style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
            >
              {t('workspace.apps.roles.unsavedChanges', {
                count: changedCellCount
              })}
            </Text>
          ) : null}
          <Button
            size="small"
            icon={<RotateCcw size={14} />}
            disabled={!dirty}
            onClick={() => setDraft(null)}
          >
            {t('workspace.apps.roles.resetToDefault')}
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<SaveIcon size={14} />}
            loading={isUpdating}
            disabled={!dirty}
            onClick={() => void onSave()}
            data-testid="permissions-save"
          >
            {t('save')}
          </Button>
        </Flex>
      }
    />
  )
}
